import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../../styles/lopsteknikk/lopsteknikk.css';

export default function LopsteknikkList() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [emailContent, setEmailContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/join/lopsteknikk-users`);
            setUsers(response.data.users);
        } catch (error) {
            setError('Failed to fetch users');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserSelect = async (user) => {
        // setSelectedUser(null);
        setSelectedUser(user);
        setError('');
        setSuccessMessage('');
        setEmailContent(`Hi ${user.name},\n\nHere's my feedback on your running technique:\n\n`);

        // try {
        //     // Fetch fresh signed URLs for the videos
        //     const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/join/lopsteknikk-user/${user.idlopstehnique}`);
        //     setSelectedUser(prev => ({
        //         ...prev,
        //         signedVideoUrls: response.data.signedVideoUrls
        //     }));
        // } catch (error) {
        //     console.error('Error fetching video URLs:', error);
        //     setError('Failed to load videos');
        // }
    };

    const handleDelete = async (user) => {
        setUserToDelete(user);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/join/lopsteknikk/${userToDelete.idlopstehnique}`);
            setUsers(users.filter(u => u.idlopstehnique !== userToDelete.idlopstehnique));
            if (selectedUser?.idlopstehnique === userToDelete.idlopstehnique) {
                setSelectedUser(null);
            }
            setSuccessMessage('User deleted successfully');
            setShowDeleteConfirm(false);
            setUserToDelete(null);
        } catch (error) {
            setError('Failed to delete user');
            console.error('Error:', error);
        }
    };

    const handleSendEmail = async () => {
        setIsSending(true);
        setError('');
        setSuccessMessage('');
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/join/send-feedback`, {
                userId: selectedUser.idlopstehnique,
                email: selectedUser.email,
                content: emailContent,
                videoUrls: selectedUser.signedVideoUrls
            });
            
            const updatedUsers = users.map(user => {
                if (user.idlopstehnique === selectedUser.idlopstehnique) {
                    return {
                        ...user,
                        feedbackSent: true,
                        feedbackMessage: emailContent
                    };
                }
                return user;
            });
            
            setUsers(updatedUsers);
            setSuccessMessage('Feedback sent successfully!');
            setTimeout(() => {
                setSelectedUser(null);
                setEmailContent('');
                setSuccessMessage('');
            }, 2000);
        } catch (error) {
            setError('Failed to send feedback');
            console.error('Error:', error);
        } finally {
            setIsSending(false);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="lopsteknikk-container">
            <div className="header-content">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </Link>
                <h2>Løpsteknikk Evaluations</h2>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            <div className="user-cards">
                {users.map(user => (
                    <div 
                        key={user.idlopstehnique} 
                        className={`user-card ${selectedUser?.idlopstehnique === user.idlopstehnique ? 'selected' : ''} ${user.feedbackSent ? 'feedback-sent' : ''}`}
                    >
                        <div className="user-card-content" onClick={() => handleUserSelect(user)}>
                            <h3>{user.name}</h3>
                            <p>{user.email}</p>
                            <p>Submitted: {new Date(user.createdAt).toLocaleDateString()}</p>
                            {user.feedbackSent && (
                                <div className="feedback-status">
                                    <i className="fas fa-check-circle"></i>
                                    <span>Feedback sent</span>
                                </div>
                            )}
                        </div>
                        <button 
                            className="delete-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(user);
                            }}
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                    </div>
                ))}
            </div>

            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete {userToDelete.name}'s submission? This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button 
                                className="cancel-button"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setUserToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                className="delete-button"
                                onClick={confirmDelete}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedUser && (
                <div className="user-detail">
                    <h3>User Details</h3>
                    <div className="user-info">
                        <p><strong>Id:</strong> {selectedUser.idlopstehnique}</p>
                        <p><strong>Name:</strong> {selectedUser.name}</p>
                        <p><strong>Email:</strong> {selectedUser.email}</p>
                        <p><strong>Phone:</strong> {selectedUser.phoneNumber}</p>
                        <p><strong>Injury History:</strong> {selectedUser.injuiryHistory}</p>
                        <p><strong>Goals:</strong> {selectedUser.goals}</p>
                        <p><strong>Running Experience:</strong> {selectedUser.runningExperience}</p>
                    </div>

                    <div className="video-section">
                        <h4>Videos</h4>
                        <div className="video-container">
                            {console.log(selectedUser.signedVideoUrls)}
                            {selectedUser.signedVideoUrls?.map((url, index) => (
                                <video 
                                    key={url}
                                    controls
                                    className="technique-video"
                                >
                                    <source src={url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            ))}
                        </div>
                    </div>

                    {selectedUser.feedbackSent ? (
                        <div className="feedback-history">
                            <h4>Sent Feedback</h4>
                            <div className="feedback-message">
                                {selectedUser.feedbackMessage}
                            </div>
                            <div className="feedback-date">
                                Sent on: {new Date(selectedUser.feedbackDate).toLocaleString()}
                            </div>
                        </div>
                    ) : (
                        <div className="feedback-section">
                            <h4>Send Feedback</h4>
                            <textarea
                                value={emailContent}
                                onChange={(e) => setEmailContent(e.target.value)}
                                placeholder="Enter your feedback..."
                                rows="6"
                                disabled={isSending}
                            />
                            <button 
                                onClick={handleSendEmail} 
                                disabled={isSending}
                                className={isSending ? 'sending' : ''}
                            >
                                {isSending ? 'Sending...' : 'Send Feedback'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
} 